import React from 'react';
import { Box } from '@cad-arc';
import { EventPanelTab } from '../components/event-panel-tab';
import { LoadingIndicator } from '../../../common/components/loading-indicator';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { useWorkspacePanel } from '../../../panels/hooks/use-workspace-panel';
import { errorOccurredWhile } from '../../../common/helpers/error-occurred-while';
import { TabBody } from '../../../panels/entity-panels/tabs/tab-body';
import { AssistingAgenciesSection } from './components/sections/assisting-agencies-section';
import { RadioChannelsSection } from './components/sections/radio-channels-section';
import { RelatedEventsSection } from './components/sections/related-events-section';
import { SubdivisionsSection } from './components/sections/subdivisions-section';
import { ScheduledEventDetailsSection } from './components/sections/scheduled-event-details-section';
import { ServiceRotationSection } from './components/sections/service-rotation-section';
import { TimestampsSection } from './components/sections/timestamps-section';
import { EventDetailsSection } from './components/sections/event-details-section';
import { useEventMetadataStore } from '../../event-panel-form/event-metadata';
import { observer } from 'mobx-react-lite';
import { useIntegratedEventDetailsQuery } from '../../../common/hooks/use-integrated-event-details-query';

export const EventDetailsTab = observer(function EventDetailsTab(): React.ReactElement {
    const eventId = useWorkspacePanel().linkedEntityId;

    const { improvedScheduledEvents, radioChannels } = useEnabledFeatures();

    const eventMetadata = useEventMetadataStore().state.metadata;
    const isScheduled = !!eventMetadata?.isScheduledEvent;

    const { error, data, isInitialLoading } = useIntegratedEventDetailsQuery({
        eventId,
        enabled: !!eventId,
    });

    return (
        <EventPanelTab title="Event Details">
            {error ? (
                <Box>{errorOccurredWhile('loading the page')}</Box>
            ) : isInitialLoading || !data ? (
                <LoadingIndicator />
            ) : (
                <TabBody>
                    <EventDetailsSection
                        isExternal={data.isExternal}
                        reportingEventNumbers={data.reportingEventNumbers}
                        agencyId={data.agencyId}
                        dispatcher={data.dispatcher}
                        callTaker={data.callTaker}
                        isScheduled={isScheduled}
                    />
                    {improvedScheduledEvents && isScheduled ? (
                        <ScheduledEventDetailsSection receivedDateUtc={data.receivedDateUtc} />
                    ) : (
                        <TimestampsSection
                            receivedDateUtc={data.receivedDateUtc}
                            initiatedDateTimeUtc={data.initiatedDateTimeUtc}
                            transmittedDateUtc={data.transmittedDateUtc}
                            firstDispatchDateUtc={data.firstDispatchDateUtc}
                            dispatchDateUtc={data.dispatchDateUtc}
                            arrivalDateUtc={data.arrivalDateUtc}
                            firstArrivalDateUtc={data.firstArrivalDateUtc}
                            isScheduled={isScheduled}
                        />
                    )}
                    <SubdivisionsSection
                        eventClass={data.eventClass}
                        dispatchArea={data.dispatchArea}
                        agency={data.agency}
                        subdivision1Label={data.subdivision1Label}
                        subdivision2Label={data.subdivision2Label}
                        subdivision3Label={data.subdivision3Label}
                        subdivision4Label={data.subdivision4Label}
                        subdivision5Label={data.subdivision5Label}
                        subdivision1={data.subdivision1}
                        subdivision2={data.subdivision2}
                        subdivision3={data.subdivision3}
                        subdivision4={data.subdivision4}
                        subdivision5={data.subdivision5}
                    />
                    {radioChannels && (
                        <RadioChannelsSection
                            agencyTypeGlobalAttrId={data.agencyTypeGlobalAttrId}
                            childAdditionalRadioChannels={data.childAdditionalRadioChannels}
                            radioChannelOptions={data.radioChannelOptions}
                        />
                    )}
                    <AssistingAgenciesSection assistingAgencies={data.assistingAgencies} />
                    <ServiceRotationSection serviceRotationGroups={data.serviceRotationGroups} />
                    <RelatedEventsSection
                        crossClassEvents={data.crossClassEvents}
                        copiedEvents={data.copiedEvents}
                        associatedEvents={data.associatedEvents}
                        bolos={data.associatedBolos}
                    />
                </TabBody>
            )}
        </EventPanelTab>
    );
});
